import { useState, type MouseEvent } from 'react';
import type { ZzzfunConfig } from '@/hooks/useZzzfunConfig';
import type { ZzzfunTheme } from '@/hooks/useZzzfunTheme';
import { zzzfunNavItems, scrollToZzzfunSection } from './zzzfun-navigation';

interface ZzzfunHeaderProps {
  config: ZzzfunConfig;
  theme: ZzzfunTheme;
  onToggleTheme: () => void;
  onOpenDownload: () => void;
}

export default function ZzzfunHeader({
  config,
  theme,
  onToggleTheme,
  onOpenDownload,
}: ZzzfunHeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);

  const handleNavClick = (event: MouseEvent<HTMLAnchorElement>, id: string) => {
    event.preventDefault();
    setMenuOpen(false);
    scrollToZzzfunSection(id);
  };

  return (
    <header className="zzzfun-header-fixed z-50 border-b border-background-200/70 bg-background-50 lg:bg-background-50/90 lg:backdrop-blur-md">
      <div className="mx-auto flex h-16 w-full items-center gap-2 px-4 md:h-20 md:px-6">
        {/* 左侧：桌面端导航 / 移动端菜单按钮 */}
        <div className="flex flex-1 items-center gap-6">
          <nav className="hidden items-center gap-6 lg:flex">
            {zzzfunNavItems.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                onClick={(event) => handleNavClick(event, item.id)}
                className="whitespace-nowrap text-sm font-medium text-foreground-700 transition-colors hover:text-primary-600"
              >
                {item.label}
              </a>
            ))}
          </nav>
          <button
            type="button"
            aria-label="打开导航菜单"
            onClick={() => setMenuOpen((prev) => !prev)}
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md text-foreground-800 transition-colors hover:bg-background-100 lg:hidden"
          >
            <i className={menuOpen ? 'ri-close-line text-2xl' : 'ri-menu-line text-2xl'} />
          </button>
        </div>

        {/* 中间：品牌 icon + 域名 */}
        <a
          href={config.officialSite}
          className="flex shrink-0 cursor-pointer items-center gap-2"
          aria-label="访问ZzzFun官网ZzzFun.net"
        >
          <span className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-xl border border-background-200/70 bg-background-100 md:h-11 md:w-11">
            <img
              src="/images/zzzfun-logo.png"
              alt="ZzzFun官方图标"
              title="ZzzFun动漫官网下载 - ZzzFun.net"
              className="h-7 w-7 object-contain md:h-8 md:w-8"
            />
          </span>
          <span className="font-label text-lg font-bold tracking-tight text-foreground-950 md:text-xl">
            ZzzFun<span className="text-primary-600">.net</span>
          </span>
        </a>

        {/* 右侧：主题切换 + 下载按钮 */}
        <div className="flex flex-1 items-center justify-end gap-2 md:gap-3">
          <button
            type="button"
            onClick={onToggleTheme}
            aria-label={theme === 'dark' ? '切换到白天模式' : '切换到夜间模式'}
            title={theme === 'dark' ? '切换到白天模式' : '切换到夜间模式'}
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-200/70 bg-background-100 text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
          >
            <i className={theme === 'dark' ? 'ri-sun-line text-xl' : 'ri-moon-line text-xl'} />
          </button>
          <button
            type="button"
            onClick={onOpenDownload}
            className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950 md:px-4"
          >
            <i className="ri-download-2-line text-lg" />
            <span className="hidden sm:inline">下载客户端</span>
          </button>
        </div>
      </div>

      {/* 移动端导航面板 */}
      {menuOpen ? (
        <div className="border-t border-background-200/70 bg-background-50 px-4 py-3 lg:hidden">
          <nav className="flex flex-col">
            {zzzfunNavItems.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                onClick={(event) => handleNavClick(event, item.id)}
                className="whitespace-nowrap rounded-md px-2 py-3 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-600"
              >
                {item.label}
              </a>
            ))}
          </nav>
        </div>
      ) : null}
    </header>
  );
}